import React, { useEffect, useRef, useState } from 'react';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';

import { getReviewTimeFrameLabel } from '../../../constants/reviewConstants';
import AppSnackbar from '../../common/popups/AppSnackbar';
import ReviewModal from '../modals/ReviewModal';
import useParentAuth from '../../../hooks/useParentAuth';
import useAuth from '../../../hooks/useAuth';
import { canAccessStudentClasses } from '../../../utils/auth';

const getReviewerLabel = (review = {}) => {

  console.log(review.reviewer_role);
  const value = review.reviewer_role;

  if (value === 3) return 'Parent';
  if (value === 4) return 'Student';

  return '';
};

function ReviewsSection({ teacher, onReviewSubmitted }) {
  const { user, isAuthenticated } = useAuth();
  const { user: parentUser, isAuthenticated: parentAuthenticated } = useParentAuth();
  const activeUser = parentUser ?? user;
  const canReview = (isAuthenticated || parentAuthenticated) && canAccessStudentClasses(activeUser?.role);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const sectionRef = useRef(null);

  useEffect(() => {
    if (window.location.hash !== '#studentReviewsSection') return;

    const frame = window.requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [teacher]);

  const handleReviewSuccess = async () => {
    setSnackbar({
      open: true,
      message: 'Review submitted successfully!',
      severity: 'success',
    });

    await onReviewSubmitted?.();
  };

  return (
    <div ref={sectionRef} id="studentReviewsSection" className="pt-10">
      <div className="mb-6 flex items-start justify-between gap-4">
        <h2 className="uppercase text-2xl flex items-center">
          <span className="bg-gradient-primary w-2 h-8 mr-3 rounded-full"></span>
          STUDENT REVIEWS
        </h2>
        {canReview ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-9 items-center rounded-full border border-[#f6911d] px-4 text-sm font-semibold text-[#f6911d] transition-colors hover:bg-[#f6911d]/10"
          >
            Add Review
          </button>
        ) : null}
      </div>

      <hr className="mt-5 mb-8 border-white/20 w-full" />

      <div className="space-y-4 max-h-96 overflow-y-auto sm:px-10">
        {teacher.reviews && teacher.reviews.length > 0 ? (
          teacher.reviews.map((review, idx) => (
            <div key={idx} className="bg-gradient-glass rounded-lg p-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between mb-3 gap-3">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <img
                    src={review.avatar ?? '/assets/images/profile_placeholder.jpg'}
                    alt={review.reviewer_name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="text-center sm:text-left">
                    <div className="flex flex-col items-center gap-1 sm:items-start">
                      <h4 className="text-white font-semibold capitalize">
                        {review.reviewer_name}
                      </h4>
                      {getReviewerLabel(review) ? (
                        <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/60">
                          {getReviewerLabel(review)}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex justify-center sm:justify-start text-orange-500">
                      {[...Array(5)].map((_, i) => (
                        <StarRateRoundedIcon
                          key={i}
                          className={i < review.rating ? 'text-primary' : 'text-gray-400/50'}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="rounded-full bg-orange-500 px-3 py-1 text-xs text-white">
                    {getReviewTimeFrameLabel(review)}
                  </span>
                  <span className="text-xs font-small text-orange-300">
                    {review.created_at}
                  </span>
                </div>
              </div>
              <p className="mb-2 text-center text-sm font-medium text-yellow-400 sm:text-left">
                {review.title}
              </p>
              <p className="text-gray-400 text-sm text-center sm:text-left">{review.comment}</p>
            </div>
          ))
        ) : (
          <div className="text-center py-10 bg-gradient-glass rounded-lg">
            <p className="text-gray-400 text-lg">No reviews yet.</p>
          </div>
        )}
      </div>

      <ReviewModal
        open={open}
        teacherId={teacher.id}
        onClose={() => setOpen(false)}
        onSuccess={handleReviewSuccess}
      />

      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
}

export default ReviewsSection;
